import React, { useState } from 'react';
import { BudgetProposalWithResourcesT } from 'routes/BudgetModule/config/types';
import { AutocompletAble } from 'ui/DropdownItems/types';
import FirstDuplicateArgumentsStep from './steps/FirstDuplicateArgumentsStep';
import SecondDuplicateArgumentsStep from './steps/SecondDuplicateArgumentsStep';
import { BudgetBys } from 'business/constants';
import { useParams } from 'react-router-dom';
import { BudgetSettingsUrlParams } from '../../BudgetSettings';
import { useDuplicateArguments } from './hooks';
import ThirdDuplicateArgumentsStep from './steps/ThirdDuplicateArgumentsStep';
import ConfirmationStep from '../../shared/ConfirmationStep';
import { trackEventArgumentDuplicationConfirmation } from '../trackersHelpers';
import { StyMultiStepModal } from '../../shared/StyMultiStepModal';
import { useTranslation } from 'react-i18next';

type FilterConfigT = {
    resource: boolean;
    section: boolean;
    budget: boolean;
};

type Props = {
    isModalShown: boolean;
    setShowModal: DispatchState<boolean>;
    budgetProposals: BudgetProposalWithResourcesT[];
};

export default function DuplicateArgumentsModal({
    isModalShown,
    setShowModal,
    budgetProposals,
}: Props): React.ReactElement {
    const { t } = useTranslation();
    const { by: selectedBy = BudgetBys.GESTIONNAIRE } =
        useParams<BudgetSettingsUrlParams>();

    //STEP 1
    const [selectedSourceBudgetProposal, setSelectedSourceBudgetProposal] =
        useState<AutocompletAble<BudgetProposalWithResourcesT> | null>(null);
    const [
        selectedSourceResourceSoftwareId,
        setSelectedSourceResourceSoftwareId,
    ] = useState<AutocompletAble<string> | null>(null);

    const [selectedArgumentIds, setSelectedArgumentIds] = useState<
        AutocompletAble<number>[] | null
    >(null);

    //STEP 2
    const [selectedTargetBudgetProposals, setSelectedTargetBudgetProposals] =
        useState<AutocompletAble<BudgetProposalWithResourcesT>[] | null>(null);
    const [
        selectedTargetResourceSoftwareIds,
        setSelectedTargetResourceSoftwareIds,
    ] = useState<AutocompletAble<string>[] | null>(null);

    // STEP 3
    const [filterConfig, setFilterConfig] = useState<FilterConfigT>({
        resource: true,
        section: true,
        budget: true,
    });

    const { mutate: duplicateArguments, isPending } = useDuplicateArguments(
        selectedArgumentIds?.map((e) => e.value),
        selectedTargetBudgetProposals?.map((e) => e.value.id),
        selectedTargetResourceSoftwareIds?.map((e) => e.value),
        filterConfig,
    );

    const onConfirmAction = () => {
        duplicateArguments();
        trackEventArgumentDuplicationConfirmation(
            selectedArgumentIds?.map((e) => e.value),
            selectedTargetBudgetProposals?.map((e) => e.value.id),
            selectedTargetResourceSoftwareIds?.map((e) => e.value),
        );
    };

    const steps = [
        {
            confirmAction: {
                enabled:
                    selectedSourceBudgetProposal !== null &&
                    selectedSourceResourceSoftwareId !== null &&
                    selectedArgumentIds !== null,
                label: 'common.next',
            },
            stepBackAction: {
                onClick: () => {
                    setShowModal(false);
                },
                enabled: true,
            },
            node: (
                <FirstDuplicateArgumentsStep
                    selectedSourceBudgetProposal={selectedSourceBudgetProposal}
                    setSelectedSourceBudgetProposal={
                        setSelectedSourceBudgetProposal
                    }
                    selectedSourceResourceSoftwareId={
                        selectedSourceResourceSoftwareId
                    }
                    setSelectedSourceResourceSoftwareId={
                        setSelectedSourceResourceSoftwareId
                    }
                    selectedArgumentIds={selectedArgumentIds}
                    setSelectedArgumentIds={setSelectedArgumentIds}
                    budgetProposals={budgetProposals}
                    by={selectedBy}
                />
            ),
        },
        {
            confirmAction: {
                enabled:
                    selectedTargetBudgetProposals !== null &&
                    selectedTargetResourceSoftwareIds !== null,
                label: 'common.next',
            },
            stepBackAction: {
                enabled: true,
            },
            node: (
                <SecondDuplicateArgumentsStep
                    selectedTargetBudgetProposalIds={
                        selectedTargetBudgetProposals
                    }
                    setSelectedTargetBudgetProposalIds={
                        setSelectedTargetBudgetProposals
                    }
                    selectedTargetResourceSoftwareIds={
                        selectedTargetResourceSoftwareIds
                    }
                    setSelectedTargetResourceSoftwareIds={
                        setSelectedTargetResourceSoftwareIds
                    }
                    budgetProposals={budgetProposals}
                    by={selectedBy}
                />
            ),
        },
        {
            confirmAction: {
                enabled: true,
                label: 'common.next',
            },
            stepBackAction: {
                enabled: true,
            },
            node: (
                <ThirdDuplicateArgumentsStep
                    budgetProposals={budgetProposals}
                    filterValues={filterConfig}
                    setFilterValues={setFilterConfig}
                    resource={selectedSourceResourceSoftwareId?.value}
                    by={selectedBy}
                />
            ),
        },
        {
            confirmAction: {
                enabled:
                    selectedTargetBudgetProposals !== null &&
                    selectedTargetResourceSoftwareIds !== null &&
                    selectedArgumentIds !== null,
                label: 'common.confirm',
                onClick: onConfirmAction,
            },
            stepBackAction: {
                enabled: true,
            },
            disableClose: isPending,
            node: (
                <ConfirmationStep
                    title={t('adminBudgetSpace.duplicateArguments.title')}
                    text={t('adminBudgetSpace.duplicateArguments.textConfirm')}
                />
            ),
        },
    ];
    return (
        <StyMultiStepModal
            isOpen={isModalShown}
            onClose={() => {
                setShowModal(false);
            }}
            maxWidth="md"
            steps={steps}
        />
    );
}
